<template>
  <div class="flex flex-col gap-4 w-full h-full">
    <div class="flex flex-col gap-4">
      <h2 class="text-xl font-semibold primary_color">Call disposition</h2>
      <div class="flex flex-row gap-2 items-center">
      <a-checkbox
        :checked="isDispositionMandatory"
        @change="handleMandatoryChange"
        class="size-4"
      >
      </a-checkbox>
      <span class="text-sm primary_color">Make call disposition mandatory to log calls</span>
      </div>
      <p class="text-sm secondary_color">
        Customize and manage call outcomes
      </p>
    </div>
    <div
      v-if="isLoading && dispositions.length === 0"
      class="max-h-80 overflow-hidden"
    >
      <SkeletonLoader :page="loaderPages.NAV_LIST" />
    </div>
    <CustomContainer
      v-else-if="dispositions?.length > 0"
      :no-background-color="true"
      :border-hidden="true"
      class="w-1/2"
    >
      <div
        class="flex flex-col gap-2 max-h-80 overflow-y-auto add_scroll_bar"
        @scroll="handleScroll"
        ref="scrollContainer"
      >
        <div class="flex flex-col gap-2">
          <CallDispositionItem
            v-for="(disposition, index) in dispositions"
            :key="disposition.id || index"
            :disposition="disposition.disposition || disposition"
            @remove="
              removeDisposition(
                disposition.id || index,
                disposition.disposition || disposition
              )
            "
          />
          <div v-if="isLoadingMore" class="flex flex-col gap-2 p-2">
            <SkeletonLoader :page="loaderPages.NAV_LIST" />
          </div>
        </div>
      </div>
    </CustomContainer>
    
    <div class="mt-3">
      <a-button
        @click="showAddModal = true"
        class="secondary_medium w-fit"
        :loading="isCreating"
      >
        <ThemeImage
          :icon="imageNames.ADD_GREY"
          alt="add_disposition"
          class="size-5"
        />
        <span class="text-[14px] font-semibold">Add disposition</span>
      </a-button>
    </div>

    <CustomModal
      title="Add Disposition"
      v-model:visible="showAddModal"
      :width="'400px'"
    >
      <template #default>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <span class="text-sm primary_color font-medium">Disposition Name</span>
            <CustomInput
              v-model="newDispositionName"
              type="primary"
              placeholder="Enter disposition name"
              format="text"
              :auto-focus="true"
              @keyup.enter="handleAddDisposition"
            />
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex flex-row gap-2 justify-end">
          <a-button @click="handleCancelAdd" class="secondary_medium">
            Cancel
          </a-button>
          <a-button
            @click="handleAddDisposition"
            class="primary_medium"
            :disabled="!newDispositionName.trim()"
            :loading="isCreating"
          >
            Add Disposition
          </a-button>
        </div>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {
  createCallDisposition,
  deleteCallDisposition,
} from "@/src/utils/callsUtils.js";
import { callSettingKeys } from "@/src/constants/callConstants.js";
import CallDispositionItem from "./CallDispositionItem.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import CallService from "@/src/api/CallService.js";
import store from "@/src/store/index.js";

const dispositions = ref([]);
const showAddModal = ref(false);
const newDispositionName = ref("");
const isLoading = ref(false);
const isCreating = ref(false);
const isLoadingMore = ref(false);
const currentPage = ref(1);
const pageSize = ref(10);
const hasMore = ref(true);
const totalCount = ref(0);
const scrollContainer = ref(null);

const isDispositionMandatory = computed(
  () => store.getters["dialerSettings/isDispositionMandatory"]
);

const fetchDispositions = async (page = 1, reset = false) => {
  try {
    if (reset) {
      isLoading.value = true;
      currentPage.value = 1;
      dispositions.value = [];
      hasMore.value = true;
    } else {
      isLoadingMore.value = true;
    }

    const params = {
      page: page,
      page_size: pageSize.value,
    };

    const response = await CallService.fetchCallDispositions(params);
    
    if (response?.data) {
      const newDispositions = response.data || [];
      
      if (reset) {
        dispositions.value = newDispositions;
      } else {
        dispositions.value = [...dispositions.value, ...newDispositions];
      }
      
      totalCount.value = response.total || 0;
      currentPage.value = page;
      
      hasMore.value = dispositions.value.length < totalCount.value;
      
    } 
  } catch (error) {
    console.error("Error fetching dispositions:", error);
    message.error("Error fetching call dispositions");
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

const removeDisposition = async (dispositionId, dispositionName) => {
  try {
    if (typeof dispositionId === "string") {
      const index = dispositions.value.findIndex(
        (d) => (d.disposition || d) === dispositionName
      );
      if (index !== -1) {
        dispositions.value.splice(index, 1);
        totalCount.value -= 1;
        message.success("Disposition removed");
      }
      return;
    }

    await deleteCallDisposition(dispositionId);
    dispositions.value = dispositions.value.filter(
      (d) => d.id !== dispositionId
    );
    totalCount.value -= 1;
    message.success("Disposition deleted successfully");
  } catch (error) {
    console.error("Error deleting disposition:", error);
    message.error("Failed to delete disposition");
  }
};

const handleScroll = (event) => {
  const { scrollTop, scrollHeight, clientHeight } = event.target;
  if (
    scrollHeight - scrollTop <= clientHeight + 1 &&
    !isLoadingMore.value &&
    hasMore.value
  ) {
    loadNextPage();
  }
};

const loadNextPage = async () => {
  if (!hasMore.value || isLoadingMore.value) return;
  
  const nextPage = currentPage.value + 1;
  await fetchDispositions(nextPage, false);
};


const handleAddDisposition = async () => {
  if (!newDispositionName.value.trim()) {
    message.warning("Please enter a disposition name");
    return;
  }

  const exists = dispositions.value.some(
    (d) =>
      (d.disposition || d).toLowerCase() ===
      newDispositionName.value.trim().toLowerCase()
  );
  
  if (exists) {
    message.warning("This disposition already exists");
    return;
  }

  try {
    isCreating.value = true;
    const disposition = await createCallDisposition(
      newDispositionName.value.trim()
    );

    if (!disposition) {
      message.error("Failed to create disposition");
      return;
    }
    
    dispositions.value.push(disposition);
    totalCount.value += 1;
    newDispositionName.value = "";
    showAddModal.value = false;
    message.success("Disposition added successfully");
    await nextTick();
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
    }
  } catch (error) {
    console.error("Error creating disposition:", error);
    message.error("Failed to create disposition");
  } finally {
    isCreating.value = false;
  }
};

const handleCancelAdd = () => {
  newDispositionName.value = "";
  showAddModal.value = false;
};

const handleMandatoryChange = async (e) => {
  const newValue = e.target.checked;
  const previousValue = isDispositionMandatory.value;
  
  try {
    const response = await store.dispatch("dialerSettings/updateSetting", {
      key: callSettingKeys.DISPOSITION_MANDATORY,
      value: newValue,
    });
    
    if (response?.success) {
      message.success("Setting updated successfully");
    } else {
      store.commit("dialerSettings/SET_SINGLE_SETTING", {
        key: callSettingKeys.DISPOSITION_MANDATORY,
        value: previousValue,
      });
      message.error("Failed to update setting");
    }
  } catch (error) {
    console.error("Error updating mandatory setting:", error);
    store.commit("dialerSettings/SET_SINGLE_SETTING", {
      key: callSettingKeys.DISPOSITION_MANDATORY,
      value: previousValue,
    });
    message.error("Failed to update setting");
  }
};

onMounted(async () => {
  await store.dispatch("dialerSettings/fetchDialerSettings");
  await fetchDispositions(1, true);
});
</script> 